import { cn } from "@/lib/utils";
import { usePipelineOverview } from "@/hooks/useDashboard";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, Layers } from "lucide-react";

export function PipelineOverview() {
  const { data: stages, isLoading } = usePipelineOverview();

  if (isLoading) {
    return (
      <div className="glass card-gradient rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-primary/10">
              <Layers className="h-4 w-4 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">Pipeline Overview</h2>
          </div>
        </div>
        <Skeleton className="h-4 w-full mb-6 rounded-full" />
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!stages || stages.length === 0) {
    return (
      <div className="glass card-gradient rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-primary/10">
              <Layers className="h-4 w-4 text-primary" />
            </div>
            <h2 className="text-lg font-semibold">Pipeline Overview</h2>
          </div>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-muted/50 flex items-center justify-center">
            <Layers className="h-6 w-6 text-muted-foreground/50" />
          </div>
          <p>No pipeline stages configured yet.</p>
        </div>
      </div>
    );
  }

  const total = stages.reduce((sum, s) => sum + s.count, 0);
  const totalValue = stages.reduce((sum, s) => sum + s.value, 0);

  return (
    <div className="glass card-gradient rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-primary/20 rounded-lg blur-md" />
            <div className="relative p-1.5 rounded-lg bg-primary/10 border border-primary/20">
              <Layers className="h-4 w-4 text-primary" />
            </div>
          </div>
          <div>
            <h2 className="text-lg font-semibold">Pipeline Overview</h2>
            <p className="text-xs text-muted-foreground">{total} active deals</p>
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-1.5 text-success text-xs mb-1">
            <TrendingUp className="h-3 w-3" />
            <span>Total Value</span>
          </div>
          <span className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            ${totalValue >= 1000000
              ? `${(totalValue / 1000000).toFixed(2)}M`
              : totalValue >= 1000
              ? `${(totalValue / 1000).toFixed(0)}K`
              : totalValue.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Stage bars with enhanced styling */}
      {total > 0 && (
        <div className="relative mb-6">
          <div className="flex h-4 rounded-full overflow-hidden bg-muted/50 border border-border/50">
            {stages.map((stage, index) => (
              <div
                key={stage.id}
                className="h-full transition-all duration-500 relative group"
                style={{
                  width: `${(stage.count / total) * 100}%`,
                  backgroundColor: stage.color,
                }}
              >
                {/* Hover tooltip */}
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <div className="bg-popover text-popover-foreground text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
                    {stage.name}: {stage.count}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stage details with enhanced styling */}
      <div className="space-y-2">
        {stages.map((stage, index) => (
          <div
            key={stage.id}
            className={cn(
              "flex items-center justify-between p-3 rounded-lg transition-all duration-200 hover:bg-muted/30 group cursor-pointer",
              index === 0 && "bg-muted/20"
            )}
          >
            <div className="flex items-center gap-3">
              <div
                className="h-3 w-3 rounded-full ring-2 ring-offset-2 ring-offset-background transition-transform group-hover:scale-110"
                style={{ backgroundColor: stage.color }}
              />
              <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                {stage.name}
              </span>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold tabular-nums">{stage.count}</span>
                <span className="text-xs text-muted-foreground">deals</span>
              </div>
              <span className="text-sm font-medium text-muted-foreground w-24 text-right tabular-nums">
                ${stage.value >= 1000 ? `${(stage.value / 1000).toFixed(0)}K` : stage.value}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
